import moment from "moment";

import { GeneratedAttachmentResponse } from "@/Common/Api/Attachments/Dto/Responses/GeneratedAttachmentResponse";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { CacheHelper } from "@/Common/Helpers/CacheHelper";
import { DocumentTreeClassControl } from "@/Common/Helpers/DocumentTreeClassControl";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { SearchMenuHelper } from "@/Common/Helpers/SearchMenuHelper";
import { MyleStaticResources } from "@/Common/Localization/MyleStaticResouces";
import { Person } from "@/Common/Models/Person/Person";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { IPortalLinkedItemModel } from "@/Common/Models/Portal/UI/IPortalLinkedItemModel";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { DocumentSortOrderType, SortDirection } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { BaseStore } from "@/Common/Stores/BaseStore";
import { RequestMapper, ServerResponseMapper } from "@/Common/Stores/Handlers/Server/RequestMapper";
import { DocumentsHelper } from "@/Documents/Helpers/DocumentsHelper";
import { DocumentStatus } from "@/Documents/Helpers/DocumentStatus";
import { UrgencyPriority } from "@/Documents/Helpers/UrgencyPriority";
import { InboundFaxMailboxModel } from "@/Documents/Models/InboundFax/InboundFaxMailboxModel";
import { MedicalDocumentItem, MedicalDocumentItemDto } from "@/Documents/Models/Medical/MedicalDocumentItem";
import { DocumentSearchSettings } from "@/Documents/Models/Settings/DocumentSearchSettings";
import { IDocumentCreateEditUIModel } from "@/Documents/Models/UI/IDocumentCreateEditUIModel";
import { IDocumentsTabModel } from "@/Documents/Models/UI/IDocumentsTabModel";
import { DocumentNote } from "../Api/Dto/Response/DocumentNote";
import { DocumentsAction } from "./Actions/DocumentsAction";
import { CreateDocumentHandler } from "./Handlers/Local/CreateDocumentHandler";
import { DocumentMiscHandler } from "./Handlers/Local/DocumentMiscHandler";
import { DocumentNotesHandler } from "./Handlers/Local/DocumentNotesHandler";
import { DocumentSearchHandler } from "./Handlers/Local/DocumentSearchHandler";
import { DocumentTableDisplaySettingsHandler } from "./Handlers/Local/DocumentTableDisplaySettingsHandler";
import { DocumentTreeHandler } from "./Handlers/Local/DocumentTreeHandler";
import { UpdateDocumentHandler } from "./Handlers/Local/UpdateDocumentHandler";
import { DocumentsResponseHandler } from "./Handlers/Server/DocumentsResponseHandler";

let defaultInstance: DocumentsStore;

class DocumentsStore extends BaseStore {
    private staticResouces: MyleStaticResources;

    // mappers and handlers
    private mapper: RequestMapper;
    private serverMapper: ServerResponseMapper;

    private docTreeMethods: DocumentTreeHandler;
    private createDocumentMethods: CreateDocumentHandler;
    private updateDocumentMethods: UpdateDocumentHandler;
    private searchOptionsMethods: DocumentSearchHandler;
    private documentNotesMethods: DocumentNotesHandler;
    private documentsTableDisplaySettingsMethods: DocumentTableDisplaySettingsHandler;
    private miscMethods: DocumentMiscHandler;
    private responseHandler: DocumentsResponseHandler;

    public DocumentsModel: IDocumentsTabModel;

    // COLUMN MANAGER POPUP MODEL
    public ColumnManagerModel: Immutable.List<ColumnConfigurationModel>;

    // column properties
    public DocumentActions: DocumentsAction;

    public timeoutReferencRefreshDocs: number;
    public timeoutReferencRefreshDocsCount: number;

    public InboundFaxInboxModel: InboundFaxMailboxModel;
    public InboundFaxTrashModel: InboundFaxMailboxModel;
    public InboundFaxIndexedModel: InboundFaxMailboxModel;

    // limits the scope of documents to this patient id
    // if nulll or empty, all patients will be handled

    constructor() {
        super();

        let self = this;

        this.timeoutReferencRefreshDocs = -1;
        this.timeoutReferencRefreshDocsCount = -1;

        this.staticResouces = new MyleStaticResources();

        this.DocumentActions = new DocumentsAction();

        this.initHandlersAndMappers();

        this.DocumentsModel = this.defaultDocumentsModel();

        this.ClientDispatchToken = LocalEventDispatcher.register((request: LocalEvent) => self.mapper.Map(request, self));

        this.ServerDispatchToken = ServerResponseDispatcher.register((response: ServerEvent) => { self.serverMapper.Map(response, self); });

        this.InitializeForGlobalDocuments(false);

        this.SaveState();
    }

    public static createSingleton(): DocumentsStore {
        return this.createStoreSingleton(defaultInstance, () => defaultInstance = new DocumentsStore());
    }

    public static createSingletonFrom(store: DocumentsStore, copy: boolean = true): DocumentsStore {
        return copy
            ? Object.assign(this.createSingleton(), store)
            : this.createStoreSingleton(defaultInstance, () => defaultInstance = store);
    }

    public InitializeForGlobalDocuments(isFromPatient: boolean, overrideColumnOrder: boolean = false): void {
        if (this.DocumentsModel.get("Initialized") === false) {
            let statusList = DocumentStatus.StatusListAsCheckboxUIModel();
            let urgencyList = UrgencyPriority.UrgencyListAsCheckboxUIModel();
            let selectedDoc = this.DocumentsModel.get("SelectedDocument");
            const classesList = DocumentTreeClassControl.GetDocumentClassesAsCheckboxUIModel(CheckboxTarget.Myle_Documents_Classes);
            selectedDoc = selectedDoc.set("Clinicians", Immutable.List(cacheStore.Clinicians));
            let docClasses = DocumentTreeClassControl.GetDocumentClasses();

            this.DocumentsModel = this.DocumentsModel
                .set("DeactivatedAccountCliniciansCache", this.getDefaultGlobalCliniciansCache(true))
                .set("DocumentDescription", Immutable.List(this.staticResouces.GetDocumentDescription().map(x => new SearchableItemModelT<string>(x))))
                .set("Classes", Immutable.List(classesList))
                .set("Status", Immutable.List(statusList))
                .set("Urgency", Immutable.List(urgencyList))
                .set("SelectedDocument", selectedDoc)
                .set("IsFromPatient", isFromPatient)
                .set("DocumentClasses", docClasses)
                .set("IsAttachInProcess", false)
                .set("IsVisible", true);

            if (!isFromPatient)
                this.DocumentsModel = this.DocumentsModel
                    .set("DateFrom", moment().subtract(5, "years").toDate())
                    .set("DateTo", moment().toDate());

            let docSources = cacheStore.DocumentSources != null ? Immutable.List(cacheStore.DocumentSources.map(x => new SearchableItemModelT<string>(x))) : Immutable.List([]);
            this.DocumentsModel = this.DocumentsModel.set("DocumentSources", docSources);

            this.SetDefaultSearchFilters(isFromPatient);

            let inboundFaxMailBoxModel = new InboundFaxMailboxModel();
            if (cacheStore.UserSettings.InboundFaxMailboxFilterSettings) {
                inboundFaxMailBoxModel.filteringSettings = cacheStore.UserSettings.InboundFaxMailboxFilterSettings;
            }

            this.InboundFaxInboxModel = inboundFaxMailBoxModel;
            this.InboundFaxTrashModel = inboundFaxMailBoxModel;
            this.InboundFaxIndexedModel = inboundFaxMailBoxModel;

            this.DocumentsModel = this.DocumentsModel.set("Initialized", true);
        }
        else {
            this.DocumentsModel = this.DocumentsModel.set("DocumentClasses", DocumentTreeClassControl.GetDocumentClasses(this.DocumentsModel.get("DocumentClasses")));
        }

        if (overrideColumnOrder)
            this.DocumentsModel = this.DocumentsModel
                .set("SearchResultsSortColumn", isFromPatient ? DocumentSortOrderType.DocumentDate : DocumentSortOrderType.AttachedOnDate)
                .set("SearchResultsSortColumnDirection", SortDirection.DESC);
    }

    public getDefaultGlobalCliniciansCache(inactveOnly?: boolean): Immutable.List<EmployeeUIModel> {
        let fullList = SearchMenuHelper.GetCliniciansList();
        let properList = this.UpdateVisibilityToCliniciansFromSelectedSites(Immutable.List(fullList));
        properList = properList.map(c => {
            if (c.ContactId === sessionStore.individualContactId) {
                c.Checked = true;
            }

            return c;
        }).toList();

        if (inactveOnly)
            properList = properList.filter(x => x.isInactive()).toList();

        return properList;
    }

    public getCliniciansCache(): Immutable.List<EmployeeUIModel> {
        return this.DocumentsModel.get("SelectedView") === "deactivatedAccount"
            ? this.DocumentsModel.get("DeactivatedAccountCliniciansCache")
            : this.DocumentsModel.get("CliniciansGlobalCache");
    }

    public setCliniciansCache(clinicians: Immutable.List<EmployeeUIModel>): void {
        this.DocumentsModel = this.DocumentsModel.get("SelectedView") === "deactivatedAccount"
            ? this.DocumentsModel.set("DeactivatedAccountCliniciansCache", clinicians)
            : this.DocumentsModel.set("CliniciansGlobalCache", clinicians);
    }

    public SearchDocuments(): void {
        this.DocumentActions.SearchDocuments(this.DocumentsModel);
    }

    private defaultSearchSettings(): DocumentSearchSettings {
        const isAnnotationEnabled: boolean = cacheStore.ClinicSettings.EnableDocumentInAppView;

        return new DocumentSearchSettings({
            ClinicianIds: [sessionStore.individualContactId],
            SiteIds: cacheStore.SiteCache.map(x => x.Model.Id),
            ClassIds: DocumentTreeClassControl.GetDocumentClassesAsCheckboxUIModel(CheckboxTarget.Myle_Documents_Classes).map(x => x.Id),
            Status: isAnnotationEnabled ? [DocumentStatus.StatusNotApproved.Id, DocumentStatus.StatusAnnotated.Id] : [DocumentStatus.StatusNotApproved.Id],
            Urgency: UrgencyPriority.UrgencyListAsCheckboxUIModel().map(x => x.Id),
            IsAllSitesChecked: false,
            IsAllCliniciansChecked: false,
            IsAllStatusChecked: false,
            IsAllUrgencyChecked: false,
            IsAllSentByChecked: false,
            IsAllPortalChecked: false,
            SelectAllClassesChecked: false,
            ShowOnlySelectedSite: false,
            SentByIds: [],
            PortalStatus: PortalHelper.getPortalStatusCheckableList(CheckboxTarget.Myle_Documents_PortalStatus).map(x => x.Id),
            PageSize: 50
        });
    }

    public SetDefaultSearchFilters(isFromPatient: boolean): void {
        const search = (cacheStore.UserSettings && isFromPatient ? cacheStore.UserSettings.PatientDocumentSearchSettings : cacheStore.UserSettings.DocumentSearchSettings) || this.defaultSearchSettings();
        const model = this.DocumentsModel;
        const modelInitialized = model.get("Initialized");

        // get sites
        let sites = model.get("Sites") && model.get("Sites").toArray();
        if (isArrayNullOrUndefinedOrEmpty(sites)) {
            sites = CacheHelper.LoadSitesFromCache(CheckboxTarget.Myle_Documents_Sites);
            if (!isArrayNullOrUndefinedOrEmpty(search && search.SiteIds))
                sites.mutate(s => s.Checked = search.SiteIds.contains(s.Id));
        }

        // get clinicians
        let clinicians = model.get("CliniciansGlobalCache") && model.get("CliniciansGlobalCache").toArray();
        if (isArrayNullOrUndefinedOrEmpty(clinicians)) {
            clinicians = this
                .UpdateVisibilityToCliniciansFromSelectedSites(SearchMenuHelper.GetCliniciansList().toImmutable(), sites.toImmutable())
                .toArray();
            if (!isArrayNullOrUndefinedOrEmpty(search.ClinicianIds)) {
                clinicians.mutate(p => p.Checked = search.ClinicianIds.contains(p.Id));
            }
        }

        // get DocumentClasses
        let classes = model.get("Classes") && model.get("Classes").toArray();
        if (!modelInitialized && !isArrayNullOrUndefinedOrEmpty(search.ClassIds)) {
            classes.mutate(p => p.Checked = search.ClassIds.contains(p.Id));
        }

        // get urgency checks
        let urgencyChecks = model.get("Urgency") && model.get("Urgency").toArray();
        if (!modelInitialized && !isArrayNullOrUndefinedOrEmpty(search.Urgency)) {
            urgencyChecks.mutate(u => u.Checked = search.Urgency.contains(u.Id));
        }

        // get 'select all *'
        let selectAllSites = model.get("SelectAllSitesChecked") ?? search.IsAllSitesChecked;
        const selectAllUrgency = model.get("SelectAllUrgencyChecked") ?? search.IsAllUrgencyChecked;
        let selectAllClinicians = model.get("SelectAllCliniciansChecked") ?? search.IsAllCliniciansChecked;
        let selectAllStatuses = model.get("SelectAllStatusChecked") ?? search.IsAllStatusChecked;
        const selectAllClasses = model.get("SelectAllClassesChecked") ?? search.SelectAllClassesChecked;

        // get status
        let statuses = model.get("Status") && model.get("Status").toArray();
        if (!modelInitialized && !isArrayNullOrUndefinedOrEmpty(search.Status)) {
            statuses = statuses.mutate(s => s.Checked = search.Status.contains(s.Id));
        }

        // get page size
        let pageSize = model.get("SelectedPageSizeOption");
        if (!modelInitialized && !IsNullOrUndefined(search.PageSize))
            pageSize = search.PageSize;

        // get 'show only selected sites'
        const showOnlySelectedSites = model.get("ShowOnlySelectedSites") ?? search.ShowOnlySelectedSite;

        // get portal status info
        let selectAllSentByChecked = model.get("SelectAllSentByChecked");
        let selectAllPortalStatus = isFromPatient && (search.IsAllPortalChecked ?? model.get("SelectAllPortalStatus"));
        let portalStatuses = model.get("PortalStatuses") && model.get("PortalStatuses").toArray();
        let sentBy = model.get("SentByGlobalCache") && model.get("SentByGlobalCache").toArray();

        if (cacheStore.ClinicSettings_Portal.EnablePortalWebsite) {
            // get 'select all sent by'
            selectAllSentByChecked ??= search.IsAllSentByChecked;

            // get 'select all Portal statuses'
            selectAllPortalStatus ??= search.IsAllPortalChecked;

            // get Portal statuses
            if (isArrayNullOrUndefinedOrEmpty(portalStatuses)) {
                portalStatuses = PortalHelper.getPortalStatusCheckableList(CheckboxTarget.Myle_Documents_PortalStatus);
                if (!isArrayNullOrUndefinedOrEmpty(search.PortalStatus))
                    portalStatuses.mutate(p => p.Checked = search.PortalStatus.contains(p.Id));
                else
                    portalStatuses.mutate(p => p.Checked = false);
            }

            // get sent by
            if (isArrayNullOrUndefinedOrEmpty(sentBy)) {
                sentBy = this
                    .UpdateVisibilityToCliniciansFromSelectedSites(
                        SearchMenuHelper.GetCliniciansList(CheckboxTarget.Myle_Documents_SentBy).toImmutable(),
                        sites.toImmutable()
                    )
                    .toArray();
                if (!isArrayNullOrUndefinedOrEmpty(search.SentByIds))
                    sentBy.mutate(p => p.Checked = search.SentByIds.contains(p.Id));
            }
        }
        else {
            selectAllSentByChecked = false;
            selectAllPortalStatus = false;
            portalStatuses = portalStatuses || [];
            sentBy = sentBy || [];
        }

        // reset for new patient view and there is no any saved user setting
        if (!modelInitialized && isFromPatient && !cacheStore.UserSettings.PatientDocumentSearchSettings) {
            selectAllClinicians = true;
            selectAllSites = true;
            selectAllStatuses = true;
            selectAllPortalStatus = true;
            selectAllSentByChecked = false;

            clinicians.mutate(p => p.Checked = true);
            sites.mutate(p => p.Checked = true);
            statuses.mutate(p => p.Checked = true);
            urgencyChecks.mutate(p => p.Checked = true);
            portalStatuses.mutate(p => p.Checked = true);
            sentBy.mutate(p => p.Checked = false);
        }

        // apply
        this.DocumentsModel = model
            .set("SelectAllCliniciansChecked", selectAllClinicians)
            .set("SelectAllSitesChecked", selectAllSites)
            .set("SelectAllStatusChecked", selectAllStatuses)
            .set("SelectAllUrgencyChecked", selectAllUrgency)
            .set("SelectAllClassesChecked", selectAllClasses)
            .set("SelectedPageSizeOption", pageSize)
            .set("ShowOnlySelectedSites", showOnlySelectedSites)
            .set("CliniciansGlobalCache", clinicians.toImmutable())
            .set("Classes", classes.toImmutable())
            .set("Sites", sites.toImmutable())
            .set("Status", statuses.toImmutable())
            .set("Urgency", urgencyChecks.toImmutable())

            .set("SelectAllPortalStatus", selectAllPortalStatus)
            .set("SelectAllSentByChecked", selectAllSentByChecked)
            .set("PortalStatuses", portalStatuses.toImmutable())
            .set("SentByGlobalCache", sentBy.toImmutable())

            .set("SaveDisplaySettings", false);
    }

    private defaultDocumentsModel(isFromPatient: boolean = false): IDocumentsTabModel {
        let newDocModel: IDocumentCreateEditUIModel = Immutable.fromJS(DocumentsHelper.DefaultModelForDocumentCreateEditUIModel());
        let editDocModel: IDocumentCreateEditUIModel = Immutable.fromJS(DocumentsHelper.DefaultModelForDocumentCreateEditUIModel());

        let dm = DocumentsHelper.DefaultModelForDocumentsModel(isFromPatient ? DocumentSortOrderType.DocumentDate : DocumentSortOrderType.AttachedOnDate);
        dm.NewDocument = newDocModel;
        dm.SelectedDocument = editDocModel;

        return Immutable.fromJS(dm);
    }

    private initHandlersAndMappers(): void {
        // handlers
        this.docTreeMethods = new DocumentTreeHandler();
        this.createDocumentMethods = new CreateDocumentHandler();
        this.updateDocumentMethods = new UpdateDocumentHandler();
        this.searchOptionsMethods = new DocumentSearchHandler();
        this.documentNotesMethods = new DocumentNotesHandler();
        this.documentsTableDisplaySettingsMethods = new DocumentTableDisplaySettingsHandler();
        this.miscMethods = new DocumentMiscHandler();
        this.responseHandler = new DocumentsResponseHandler(this.DocumentActions);

        this.mapper = new RequestMapper();
        this.serverMapper = new ServerResponseMapper();

        this.mapper.AddInstance(this.docTreeMethods);
        this.mapper.AddInstance(this.createDocumentMethods);
        this.mapper.AddInstance(this.updateDocumentMethods);
        this.mapper.AddInstance(this.searchOptionsMethods);
        this.mapper.AddInstance(this.documentNotesMethods);
        this.mapper.AddInstance(this.documentsTableDisplaySettingsMethods);
        this.mapper.AddInstance(this.miscMethods);

        this.serverMapper.AddInstance(this.responseHandler);
    }

    public UpdateVisibilityToCliniciansFromSelectedSites(clinicians: Immutable.List<EmployeeUIModel>, sites: Immutable.List<SiteUIModel> = null): Immutable.List<EmployeeUIModel> {
        sites = sites || this.DocumentsModel.get("Sites");
        let checkedSiteIds = sites.filter(p => p.Checked === true).map(s => s.Id).toArray();

        return clinicians
            .map(c => {
                let include = false;
                for (let site of c.Sites) {
                    if (checkedSiteIds.indexOf(site) >= 0) {
                        include = true;
                        break;
                    }
                }
                c.Visible = include;
                return c;
            })
            .toList();
    }

    public resetNewDocument(): void {
        const newDocument: IDocumentCreateEditUIModel = Immutable.fromJS(DocumentsHelper.DefaultModelForDocumentCreateEditUIModel());
        this.DocumentsModel = this.DocumentsModel.set("NewDocument", newDocument);
    }

    public InitializeNewDocument(selectedPatient?: Person, generatedAttachment?: GeneratedAttachmentResponse): void {
        const kvpList = [
            new KeyValuePair<string, string>(GuidHelper.Empty(), ""),
            ...cacheStore.GetHRMStatusList()
        ];

        let newDocument = this.DocumentsModel.get("NewDocument");

        if (!newDocument.get("RememberClinician"))
            newDocument = newDocument.set("SelectedClinicians", Immutable.List([]));

        if (!newDocument.get("RememberDescription"))
            newDocument = newDocument.set("Description", "");

        if (selectedPatient) {
            newDocument = newDocument.set("SelectedPatients", Immutable.List([selectedPatient]));
        }
        else {
            let id = this.DocumentsModel.get("PatientContactId");
            if (id != null && id !== "") {
                var pm = patientStore.getPersonModel(id);
                newDocument = newDocument.set("SelectedPatients", Immutable.List([pm]));
            }
        }

        let docModel = new MedicalDocumentItemDto();
        docModel.SiteId = sessionStore.getCurrentEmployee().getActiveSite().Model.Id;

        let doc = new MedicalDocumentItem(docModel);

        newDocument = newDocument.set("MedicalDocumentInfo", doc);

        newDocument = newDocument.set("Clinicians", Immutable.List(cacheStore.Clinicians));

        newDocument = newDocument.set("HRMStatusList", Immutable.List(kvpList));

        newDocument = newDocument.set("SelectedHRMStatus", newDocument.get("HRMStatusList").get(0).Key);

        newDocument = newDocument.set("DocumentDate", new Date());

        let docClass = this.DocumentsModel.get("DocumentClasses").toList().get(0);

        newDocument = newDocument.set("ClassLookupId", docClass.LookupId);

        newDocument = newDocument.set("SubClassLookupId", docClass.SubClasses.toList().get(0).LookupId);

        newDocument = newDocument.set("SelectedHRMStatus", null);

        newDocument = newDocument.set("Source", "");

        if (generatedAttachment)
            newDocument = newDocument.set("ExternalAttachment", generatedAttachment);

        newDocument = newDocument
            .set("SendingFacilityId", "")
            .set("SendingFacilityReport", "")
            .set("AccompanyingSubClass", "")
            .set("AccompanyingMnemonic", "")
            .set("SourceAuthorName", "")
            .set("AuthoringDate", null)
            .set("ObservationDate", null)
            .set("IsUrgent", false)
            .set("AdditionalDetailsVisible", false)
            .set("LockUI", false);

        this.DocumentsModel = this.DocumentsModel.set("NewDocument", newDocument);
    }

    public ImportDocumentsModel(model: IDocumentsTabModel): void {
        if (model == null)
            return;

        this.DocumentsModel = model;
    }

    public SaveState(): void {
        if (!this.DocumentsModel?.get("IsAttachInProcess")) {
            if (this.DocumentsModel?.get("IsFromPatient")) {
                patientStore.saveDocumentStoreState();
            }
            else {
                cacheStore.DocumentsState = this.DocumentsModel;
            }
        }
    }

    public ReturnStateToMainTab(): void {
        if (cacheStore.DocumentsState != null)
            this.ImportDocumentsModel(cacheStore.DocumentsState);
    }

    public getCurrentModelPatientId(): string {
        return this.DocumentsModel?.get("PatientContactId") ?? "";
    }

    public updateDocumentModelVisiblity(isVisible: boolean): void {
        this.DocumentsModel = this.DocumentsModel.set("IsVisible", isVisible);
    }

    public transformDocumentToSharedItems(doc: MedicalDocumentItem): void {
        let linkedItemModel = this.DocumentsModel.get("PortalLinkedItemModel");
        this.DocumentsModel = this.DocumentsModel.set("PortalLinkedItemModel", this.updatePortalLinkedItemModel(linkedItemModel, doc));
    }

    public transformDocumentToPortalSharedItems(doc: MedicalDocumentItem): IPortalLinkedItemModel {
        let linkedItemModel: IPortalLinkedItemModel = Immutable.fromJS(PortalHelper.getDefaultPortalLinkedItemModel());
        return this.updatePortalLinkedItemModel(linkedItemModel, doc);
    }

    private convertDocumentNotesToPortalMessage(docNotes: DocumentNote[]): SharedItemMessage[] {
        if (!docNotes) return [];

        return docNotes.map(note => new SharedItemMessage({
            Text: note.NoteContent,
            SentByContactId: note.ContactId,
            DateCreated: note.NoteDate,
            CopiedFromId: note.NoteId,

            Status: PortalSyncStatus.Unsent,
            LastStatusDate: moment().toDate(),
            CreatedByUserId: sessionStore.userId,
            DateModified: moment().toDate(),
            IsSelected: false,
            IsDisabled: false
        }));
    }

    private getDocumentPortalMessages(medicalDocumentItem: MedicalDocumentItem): SharedItemMessage[] {
        let doc = medicalDocumentItem.Model;
        let portalMessages: SharedItemMessage[] = [];

        if (doc != null) {
            if (doc.SharedItemMessages != null && doc.SharedItemMessages.length > 0) {
                portalMessages = doc.SharedItemMessages.map(x => {
                    x.tmpIdForUI = SharedItemMessage.getTempIdForUi(x);
                    return x;
                });
            }
        }

        return portalMessages;
    }

    private updatePortalLinkedItemModel(linkedItemModel: IPortalLinkedItemModel, doc: MedicalDocumentItem): IPortalLinkedItemModel {
        const docModel = doc.Model;

        // ( we have to deal with sent/unsent document notes as messages, sent plain messages, new plain message and their statuses)
        let documentNotesSent: SharedItemMessage[] = this.getDocumentPortalMessages(doc);
        const docNoteSentIds = documentNotesSent.map(x => x.CopiedFromId); // document notes portal messages use original document note id as their own ids.
        let documentNotesUnsent = this.convertDocumentNotesToPortalMessage(docModel.NoteInfoCollection).filter(x => docNoteSentIds.indexOf(x.CopiedFromId) == -1);
        let regularMessagesSent: SharedItemMessage[] = PortalHelper.getPortalMessageOfType(docModel.SharedItemMessages, PortalLinkedItemType.Document);

        // ensure proper display of document notes messages
        documentNotesUnsent.forEach(x => {
            x.IsSelected = false;
            x.IsDisabled = false;
        });
        documentNotesSent.forEach(x => {
            x.IsSelected = x.Status !== PortalSyncStatus.Revoked;
            x.IsDisabled = true;
        });
        regularMessagesSent.forEach(x => {
            x.IsDisabled = true;
        });

        // sorting
        documentNotesUnsent = ArrayHelper.SortDynamic(documentNotesUnsent, "DateCreated");
        let sentMessages = documentNotesSent.concat(regularMessagesSent);
        sentMessages = ArrayHelper.SortDynamic(sentMessages, "DateCreated");
        let messagesToShow: SharedItemMessage[] = [];
        messagesToShow = messagesToShow.concat(documentNotesUnsent);
        messagesToShow = messagesToShow.concat(sentMessages);
        messagesToShow = ArrayHelper.SortDynamic(messagesToShow, "DateCreated", true);

        let updatedLinkedItemModel = linkedItemModel
            .set("SendButtonText", docModel.SignOffDate != null ? cacheStore.GetString("SendLabel") : cacheStore.GetString("SendToPatientPortal"))
            .set("LinkedItemMessages", Immutable.List(messagesToShow))
            .set("LinkedItemType", PortalLinkedItemType.Document)
            .set("LinkedItemId", docModel.Id)
            .set("LinkedItemModel", doc)
            .set("SharedItem", docModel.SharedItem)
            .set("DefaultSubject", doc.getDefaultSubject())
            .set("ErrorMessage", null);

        if (docModel.Patients && docModel.Patients.length > 0) {
            updatedLinkedItemModel = updatedLinkedItemModel
                .set("RegistrationStatus", docModel.Patients[0].PortalSyncState)
                .set("PatientContactId", docModel.Patients[0].Id);
        }
        return updatedLinkedItemModel;
    }
}

export { DocumentsStore, defaultInstance as documentsStore };
